import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MfaStep } from '@/components/auth/MfaStep';
import { usePartnerManifest } from '@/hooks/usePartnerManifest';
import { useAuth } from '@/lib/auth/auth-context';
import type { AuthChallenge, SignInResult } from '@/lib/auth/cognito';
import { applyFavicon, resolveBrandLogoUrl } from '@/lib/favicon';

/**
 * CA-01: Sign in and install (wireframes/capture-app.html #ca01). Same
 * Cognito pool, whitelabel config, and origin as the partner's portal
 * (PRIN-SC-4): this screen is a role-scoped surface, not a second
 * identity system.
 *
 * The layout and copy mirror the portal's sign-in standard
 * (`apps/classification-ui/src/pages/SignIn.jsx`): the brand mark above a
 * "Welcome back" heading, a centred card, and the same field labels. This
 * app is token-themed rather than the portal's literal Tailwind classes
 * (CAPTURE_JOBS.md section 5), so the colours come from `--color-brand`
 * and `--color-surface` and repaint for a partner exactly as the rest of
 * the app does once `usePartnerManifest` applies their theme.
 *
 * This screen renders outside AppShell, so it carries its own measure. On
 * a phone the form is the screen and needs no frame around it. From the
 * tablet breakpoint it becomes a bordered card centred in the viewport,
 * which keeps the fields at a usable width instead of stretching one
 * email input across a full monitor.
 *
 * The password is held in local state for the length of the submit and
 * passed straight to the Cognito SDK. It is never stored, never put on a
 * query string, and never logged.
 */
export function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { manifest, loading } = usePartnerManifest();
  const { status, error, signIn, completeSignIn } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // The step Cognito asked for after the password, and the line shown when
  // its session lapsed and the form came back.
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);
  const [notice, setNotice] = useState('');

  const from = (location.state as { from?: string } | null)?.from ?? '/work';

  // The startup hook in index.html sets the build-time default before
  // this ever mounts; once the tenant's own manifest loads, its runtime
  // logo mark takes over (CAPTURE_JOBS.md section 5).
  useEffect(() => {
    if (manifest) applyFavicon(manifest);
  }, [manifest]);

  if (status === 'signed-in') {
    return <Navigate to={from} replace />;
  }

  const appName = `${manifest?.displayName ?? 'AICA'} Smart Capture`;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setNotice('');
    const outcome = await signIn(email.trim(), password, appName);
    setSubmitting(false);
    if (outcome.status === 'signed-in') {
      setPassword('');
      navigate(from, { replace: true });
    } else if (outcome.status === 'challenge') {
      setPassword('');
      setChallenge(outcome.challenge);
    }
  }

  function onStepResult(result: SignInResult) {
    if (result.status === 'challenge') {
      setChallenge(result.challenge);
      return;
    }
    completeSignIn(result.session);
    navigate(from, { replace: true });
  }

  function onStepExpired(message: string) {
    setChallenge(null);
    setNotice(message);
  }

  const displayName = manifest?.displayName ?? 'AICA';
  const logoUrl = resolveBrandLogoUrl(manifest);

  return (
    <div className="flex flex-1 flex-col justify-center px-6 py-10">
      <div className="mx-auto w-full max-w-sm sm:rounded-card sm:border sm:border-border sm:p-8">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src={logoUrl} alt={displayName} className="size-14 rounded-control object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-surface-foreground">Welcome back</h1>
            <p className="mt-1 text-sm text-muted">
              {loading ? 'Loading partner branding…' : `Sign in to ${displayName} Smart Capture`}
            </p>
          </div>
        </div>

        {challenge ? (
          <MfaStep
            key={challenge.kind}
            challenge={challenge}
            email={email.trim()}
            appName={appName}
            onResult={onStepResult}
            onExpired={onStepExpired}
            onCancel={() => setChallenge(null)}
          />
        ) : (
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="operator email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          {notice || error ? (
            <p role="alert" className="text-sm text-danger">
              {notice || error}
            </p>
          ) : null}
          <Button type="submit" size="lg" disabled={submitting || status === 'restoring'}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
        )}

        <p className="mt-6 text-center text-xs text-muted">
          Add Smart Capture to your home screen for offline capture and faster launch.
        </p>
      </div>
    </div>
  );
}
